import React, { useCallback, useEffect, useState } from 'react';
import OTPInput from 'react-otp-input';
import { IHealthId, confirmAbhaOtp } from '../../api/abha/confirm-otp';
import { captureError } from '../../pages/captureError';
import { track } from '../../tracking';
import {
    AbhaFilled,
    AbhaIconNotLinked,
    cautionIcon,
    downArrowIcon,
    loaderIcon,
    upArrowIcon,
} from '../common/Icons';
import { resendAbhaOtp } from '../../api/abha/resend-otp';
import { Patient } from '../../store/types/patient';
import { linkAbha } from './linkAbha';
import { notification } from 'antd';
import { getMe, getMySingleDoctorId, getSelectedClinicId } from '../../store/idbHandler';
import { useParams } from 'react-router-dom';

const defaultTime = 30;

const ABHAOtpBox: React.FC<{
    otp: string;
    setOtp: (otp: string) => void;
    phoneNumber: string | undefined;
    setGenerateOtpState: (otpState: 'none' | 'loading' | 'success' | 'error') => void;
    generateOtpState: 'none' | 'loading' | 'success' | 'error';
    setTxnId: (txnId: string) => void;
    txnId: string | undefined;
    healthIds: IHealthId[] | null;
    setHealthIdHandler: (healthId: IHealthId[] | null) => void;
    setConfirmStateHandler: (
        val: 'none' | 'loading' | 'error' | 'options' | 'no-options' | 'linking',
    ) => void;
    confirmState: 'none' | 'loading' | 'error' | 'options' | 'no-options' | 'linking';
    selectedHealthId: IHealthId | null;
    selectedHealthIdHandler: (val: IHealthId | null) => void;
    setIsOtpDisabled: (val: boolean) => void;
    setErrorString: (val: string | undefined) => void;
    isOtpDisabled: boolean;
    errorString: string | undefined;
    isModal: boolean;
    togglePhoneNumber: (val: boolean) => void;
    patientId: string | undefined;
    setIsNameDisabled?: (val: boolean) => void;
    createFlow: (skipState?: string) => void;
    resetError: () => void;
    abhaLinkPatient?: Patient;
    closeModal?: () => void;
    onAbhaLink?: (pt: Patient) => void;
}> = ({
    otp,
    setOtp,
    setGenerateOtpState,
    setTxnId,
    txnId,
    generateOtpState,
    healthIds,
    setHealthIdHandler,
    confirmState,
    setConfirmStateHandler,
    selectedHealthId,
    selectedHealthIdHandler,
    setErrorString,
    setIsOtpDisabled,
    isOtpDisabled,
    errorString,
    isModal,
    togglePhoneNumber,
    patientId,
    setIsNameDisabled,
    resetError,
    createFlow,
    abhaLinkPatient,
    closeModal,
    onAbhaLink,
}) => {
    const [showMore, setShowMore] = useState(false);
    const [timer, setTimer] = useState(defaultTime);
    const timeOutCallback = useCallback(() => setTimer((currTimer) => currTimer - 1), []);

    const resetTimer = () => {
        if (!timer) {
            setTimer(defaultTime);
        }
    };

    useEffect(() => {
        if (txnId) {
            timer > 0 && setTimeout(timeOutCallback, 1000);
        } else {
            resetTimer();
        }
    }, [txnId, timer, timeOutCallback]);

    const params = useParams<{ cId: string }>();

    useEffect(() => {
        if (otp.length === 6 && txnId) {
            setIsOtpDisabled(true);
            setConfirmStateHandler('loading');
            togglePhoneNumber(true);

            confirmAbhaOtp(
                getMySingleDoctorId() || '',
                params.cId || getSelectedClinicId() || '',
                getMe()?.business?.id || '',
                {
                    otp,
                    txn_id: txnId,
                },
            )
                .then((res) => {
                    if (res.error) {
                        throw Error(res.error);
                    }

                    if (res.health_ids === null) {
                        if (abhaLinkPatient) {
                            linkAbha(
                                getMySingleDoctorId() || '',
                                params.cId || getSelectedClinicId() || '',
                                abhaLinkPatient,
                                txnId,
                                res.skip_state,
                                () => setConfirmStateHandler('linking'),
                                (message) => {
                                    notification.error({
                                        message: message ?? 'Something went wrong!',
                                        duration: 5,
                                    });
                                    track('ABHA_LINK_FAILED', { skip_state: res.skip_state });
                                    closeModal && closeModal();
                                },
                                () => {
                                    notification.success({
                                        message: 'ABHA linked successfully!',
                                        duration: 5,
                                    });
                                    track('ABHA_LINK_SUCCESS', { skip_state: res.skip_state });
                                    closeModal && closeModal();
                                    onAbhaLink && onAbhaLink(abhaLinkPatient);
                                },
                            );

                            return;
                        }

                        if (patientId) {
                            setIsNameDisabled && setIsNameDisabled(true);
                            setConfirmStateHandler('none');
                            createFlow(res.skip_state);
                        } else {
                            setConfirmStateHandler('no-options');
                        }
                    }

                    if (Array.isArray(res.health_ids)) {
                        selectedHealthIdHandler(res.health_ids[0]);
                        setConfirmStateHandler('options');
                    }

                    togglePhoneNumber(false);
                    setHealthIdHandler(res.health_ids as IHealthId[] | null);
                })
                .catch((e) => {
                    setIsOtpDisabled(false);
                    togglePhoneNumber(false);
                    setErrorString((e as Error)?.message ?? 'Something went wrong!');
                    setConfirmStateHandler('error');
                    resetTimer();
                    // captureError(new Error(`ABHA: ${e?.message}`));
                    track('CONFIRM_OTP_ADD_PT_ERROR', { error: e.message, txnId: txnId });
                });
        }
    }, [otp]);

    const resendOTP = () => {
        if (txnId) {
            resetTimer();
            setGenerateOtpState('loading');
            setOtp('');
            setIsOtpDisabled(true);
            setErrorString(undefined);

            resendAbhaOtp(
                getMySingleDoctorId() || '',
                params.cId || getSelectedClinicId() || '',
                getMe()?.business?.id || '',
                {
                    txn_id: txnId,
                },
            )
                .then((res) => {
                    if (res?.error) {
                        throw Error(res?.error);
                    }
                    setIsOtpDisabled(false);
                    setTxnId && setTxnId(res.txn_id);
                    setGenerateOtpState('success');
                })
                .catch((err) => {
                    setIsOtpDisabled(true);
                    setErrorString((err as Error)?.message ?? 'Something went wrong!');
                    setGenerateOtpState('error');
                    // captureError(new Error(`ABHA: ${err?.message}`));
                    track('RESENT_OTP_ADD_PT_ERROR', { error: err.message, txnId: txnId });
                });
        }
    };

    const gotoNoOptionsState = () => {
        if (abhaLinkPatient && confirmState === 'options' && txnId) {
            linkAbha(
                getMySingleDoctorId() || '',
                params.cId || getSelectedClinicId() || '',
                abhaLinkPatient,
                txnId,
                undefined,
                () => setConfirmStateHandler('linking'),
                (message) => {
                    notification.error({
                        message: message ?? 'Something went wrong!',
                        duration: 5,
                    });
                    track('ABHA_LINK_FAILED', { skip_state: undefined });
                    closeModal && closeModal();
                },
                () => {
                    notification.success({
                        message: 'ABHA linked successfully!',
                        duration: 5,
                    });
                    track('ABHA_LINK_SUCCESS', { skip_state: undefined });
                    closeModal && closeModal();
                    onAbhaLink && onAbhaLink(abhaLinkPatient);
                },
            );
            return;
        }

        if (patientId) {
            createFlow();
        } else {
            resetError();
            setConfirmStateHandler('no-options');
        }
    };

    const gotoOptionsState = () => {
        setConfirmStateHandler('options');
    };

    // create new, if no link
    return confirmState === 'linking' ? (
        <div className="flex h-full w-full flex-col items-center justify-center">
            <span className="flex flex-col gap-8 items-center gap-8">
                {loaderIcon('w-20 h-20 fill-darwin-accent-bright-blue-800 font-700 animate-spin')}
                <p className="text-darwin-accent-bright-blue-800 font-700 text-14">Linking ABHA</p>
            </span>
        </div>
    ) : confirmState === 'no-options' ? (
        <div className="flex flex-col gap-12 items-start">
            <p className={isModal ? 'text-13 font-700' : 'text-neutral-3 text-14 font-700'}>
                Patient ABHA
            </p>
            <div className={`flex items-center w-full ${isModal ? 'justify-between' : 'gap-16'}`}>
                <div className="flex p-8 items-center gap-8 rounded-8 bg-darwin-neutral_new-50">
                    {AbhaFilled()}
                    <p className="text-13">Add patient details to create</p>
                    {downArrowIcon('fill-darwin-neutral_new-1000 w-12 h-16')}
                </div>
                {healthIds && healthIds.length !== 0 && (
                    <span
                        onClick={gotoOptionsState}
                        className="flex items-center gap-4 cursor-pointer font-700 text-darwin-accent-bright-blue-800 text-13"
                    >
                        Select from existing ABHA{' '}
                        {/* {rightArrowIcon2('fill-darwin-accent-bright-blue-800 font-700 w-12 h-16')} */}
                    </span>
                )}
            </div>
        </div>
    ) : confirmState === 'options' && healthIds && Array.isArray(healthIds) ? (
        <div className={`flex flex-col items-start gap-8 ${isModal ? 'w-full' : ''}`}>
            <p className="font-700 text-13">Select patient ABHA</p>
            <div className={`flex w-full ${isModal ? 'gap-8 justify-between' : 'gap-8'}`}>
                <div
                    className={`flex flex-col border border-darwin-neutral_new-200 p-12 rounded-8 gap-8 ${
                        showMore && 'overflow-scroll hide-scollbar max-h-172'
                    } ${isModal ? 'w-full' : 'w-360 max-w-360'}`}
                >
                    {healthIds.slice(0, showMore ? healthIds.length : 2).map((h) => (
                        <div
                            onClick={() => selectedHealthIdHandler(h)}
                            className={`flex ${
                                h.health_id === selectedHealthId?.health_id
                                    ? 'bg-darwin-accent-bright-blue-100'
                                    : 'bg-white'
                            } cursor-pointer border w-full gap-8 p-8 ${
                                h.health_id === selectedHealthId?.health_id
                                    ? 'border-darwin-accent-bright-blue-800'
                                    : 'border-darwin-neutral_new-200'
                            } rounded-8`}
                        >
                            <span className="self-start">
                                {h.health_id === selectedHealthId?.health_id
                                    ? AbhaIconNotLinked
                                    : AbhaFilled()}
                            </span>
                            <div className="flex flex-col items-start">
                                <p
                                    className={`text-13 font-700 ${
                                        h.health_id === selectedHealthId?.health_id
                                            ? 'text-darwin-accent-bright-blue-800'
                                            : 'text-darwin-neutral_new-1000'
                                    }`}
                                >
                                    {h.details?.name ?? ''}
                                </p>
                                <p className="text-11 font-400 text-darwin-neutral_new-800">
                                    {h.health_id ?? ''}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
                {healthIds.length > 2 && (
                    <div
                        onClick={() => setShowMore((prev) => !prev)}
                        className="flex items-center w-80 flex-shrink-0 gap-4 self-end cursor-pointer"
                    >
                        <p className="text-darwin-accent-bright-blue-800 flex-shrink-0 whitespace-nowrap font-700 text-13">
                            Show {!showMore ? 'more' : 'less'}{' '}
                        </p>
                        {!showMore
                            ? downArrowIcon(
                                  'fill-darwin-accent-bright-blue-800 flex-shrink-0 font-700 w-12 h-12',
                              )
                            : upArrowIcon(
                                  'fill-darwin-accent-bright-blue-800 flex-shrink-0 font-700 w-13 h-13',
                              )}
                    </div>
                )}
            </div>
            <span
                onClick={gotoNoOptionsState}
                className="cursor-pointer font-700 text-darwin-accent-bright-blue-800 text-13"
            >
                Create new ABHA
            </span>
        </div>
    ) : (
        <div className="flex flex-col gap-12 items-start">
            <p className="text-13 font-700">
                {generateOtpState === 'none'
                    ? 'Enter phone number to add ABHA address'
                    : 'Enter OTP to create or link ABHA'}
            </p>

            <div className="flex items-center gap-12">
                <OTPInputBox
                    key={String(isOtpDisabled)}
                    isOtpDisabled={isOtpDisabled}
                    otp={otp}
                    setOtp={setOtp}
                />
                {(confirmState === 'loading' || generateOtpState === 'loading') && (
                    <span className="flex items-center gap-8">
                        <p className="text-darwin-accent-bright-blue-800 font-700 text-13">
                            {generateOtpState === 'loading' ? 'Sending OTP' : 'Verifying OTP'}
                        </p>
                        {loaderIcon(
                            'w-20 h-20 fill-darwin-accent-bright-blue-800 font-700 animate-spin',
                        )}
                    </span>
                )}
            </div>
            <div className="flex flex-col gap-4 items-start">
                {(generateOtpState === 'error' || confirmState === 'error') && errorString && (
                    <div className="flex items-center gap-4">
                        {cautionIcon()}
                        <p className="text-red-primary text-11">{errorString}</p>
                    </div>
                )}

                {txnId && !(confirmState === 'loading') ? (
                    ((generateOtpState === 'error' || confirmState === 'error') && errorString) ||
                    timer <= 0 ? (
                        <span
                            onClick={resendOTP}
                            className="cursor-pointer font-700 text-darwin-accent-bright-blue-800 text-12"
                        >
                            Resend OTP
                        </span>
                    ) : (
                        <span className="font-700 text-darwin-neutral-600 text-12">
                            Resend OTP in {timer} seconds...
                        </span>
                    )
                ) : null}
            </div>
        </div>
    );
};

export const OTPInputBox: React.FC<{
    otp: string;
    setOtp: (otp: string) => void;
    isOtpDisabled?: boolean;
}> = ({ otp, setOtp, isOtpDisabled = false }) => (
    <OTPInput
        value={otp}
        onChange={setOtp}
        numInputs={6}
        renderInput={(props) => <input disabled={isOtpDisabled} {...props} />}
        inputStyle={{
            width: 36,
            height: 36,
            border: '1px solid #D1D1D1',
            borderRadius: 8,
        }}
        inputType="number"
        containerStyle={{ gap: 8 }}
        shouldAutoFocus={!isOtpDisabled}
    />
);

export default ABHAOtpBox;
